import { FormHelperText } from '@mui/material'
import { useMemo } from 'react'
import { FieldError, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { FileUploader, Resource } from 'utoniq-ui'

import { ItemType } from '@/clients/utoniq-core/schema'
import {
  CreateItemProps,
  FileUploadInfo,
} from '@/components/pages/ConsoleItemCreate/template/ItemCreateForm'
import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'

type ResourceUploadCellProps = {
  resourceType: ItemType
  watch: UseFormWatch<CreateItemProps>
  fileUploadInfo: FileUploadInfo
  setValue: UseFormSetValue<CreateItemProps>
  error?: FieldError
  onClose?: (resourceType: ItemType) => void
  /** ドキュメント作成時にartist.resourceドキュメントを作るかどうか、resourceまたはwallpaperResourceをcreateする際はtrueにする */
  hasCreateResource: boolean
}
export const ResourceUploadCell: Cell<ResourceUploadCellProps> = ({
  resourceType,
  watch,
  fileUploadInfo,
  setValue,
  error,
  onClose,
  hasCreateResource,
}) => {
  const { t } = useTranslation('consoleItemCreate')
  const { type, shape } = useMemo<{
    type: 'pdf' | 'video' | 'image' | 'audio'
    shape?: 'square'
  }>(() => {
    switch (resourceType) {
      case 'magazine':
        return { type: 'pdf', shape: undefined }
      case 'video':
        return { type: 'video', shape: undefined }
      case 'audio':
        return { type: 'audio', shape: undefined }
      default:
        return { type: 'image', shape: 'square' }
    }
  }, [resourceType])

  const name = useMemo(() => {
    switch (resourceType) {
      case ItemType.Wallpaper:
        return 'wallpaperResource'
      case ItemType.Avatar:
        return 'avatarResource'
      default:
        return 'resource'
    }
  }, [resourceType])

  if (resourceType === ItemType.Merch) {
    return null
  }

  return (
    <CellFrame sx={{ pb: 8 }}>
      <FileUploader
        sx={{ p: 0 }}
        shape={shape}
        resource={watch(name) as Resource | undefined}
        t={t}
        fileUploadInfo={{
          type,
          onDrop() {
            return fileUploadInfo
              .onDrop(resourceType, hasCreateResource)
              .then(resource => {
                if (!resource) {
                  return
                }

                switch (resourceType) {
                  case ItemType.Wallpaper:
                    // createの際のミューテーション引数としてartist.resourceドキュメントのIDをフォーム値に設定する。
                    setValue(
                      'wallpaperResources.wallpaperResourceId',
                      resource.id as never,
                      { shouldValidate: true }
                    )
                    // updateの際はtmpPathをミューテーション引数に指定するため、imageUploadPathのフォーム値を更新する
                    setValue(
                      'wallpaperResource.imageUploadPath',
                      resource.imageUploadPath as never,
                      { shouldValidate: true }
                    )
                    break
                  case ItemType.Avatar:
                    setValue(
                      'avatarResources.avatarResourceId',
                      resource.id as never,
                      { shouldValidate: true }
                    )
                    break
                  default:
                    setValue('resourceId', resource.id, {
                      shouldValidate: true,
                    })
                    setValue(
                      'resource.imageUploadPath',
                      resource.imageUploadPath as never,
                      { shouldValidate: true }
                    )
                    break
                }
                setValue(name, { ...resource })
                return resource
              })
          },
          progress: fileUploadInfo.progress || 0,
          description: t(`${resourceType}UploaderDescription`),
          onClose() {
            if (![ItemType.Avatar, ItemType.Wallpaper].includes(resourceType)) {
              setValue('resource', null, { shouldDirty: true })
            }
            setValue('resourceId', '', {
              shouldValidate: true,
              shouldDirty: true,
            })
            setValue('wallpaperResources.wallpaperResourceId', '' as never, {
              shouldValidate: true,
              shouldDirty: true,
            })
            setValue('avatarResources.avatarResourceId', '' as never, {
              shouldValidate: true,
              shouldDirty: true,
            })
            setValue('avatarResource', null, { shouldDirty: true })
            setValue('wallpaperResource', null, { shouldDirty: true })
            onClose && onClose(resourceType)
          },
        }}
        content={{
          title: t(`${resourceType}UploaderTitle`),
        }}
      />
      {!!error && <FormHelperText error>{error.message}</FormHelperText>}
    </CellFrame>
  )
}
